import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Button,
  Alert,
  Container,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SchoolIcon from '@mui/icons-material/School';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BillingAllPurchases from '../../../components/BillingAllPurchases';
import SubscriptionList from '../../../components/SubscriptionList';
import MockExamSubscriptionList from '../../../components/MockExamSubscriptionList';
import MobileSubscriptionList from '../../../components/MobileSubscriptionList';
import PaperPurchases from '../PaperPurchases';
import ParentRefundRequests from './ParentRefundRequests';
import useRefundRequestsEnabled from '../../../hooks/useRefundRequestsEnabled';
import api from '../../../api';
import { sanitizeStripeCheckoutUrl } from '../../../utils/stripeCheckoutUrl';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';

// Tab order — index in this array is the MUI Tabs value, and the value is the URL segment.
// "all" leads: a parent whose only purchase was a mock exam used to land on Course and read
// "No subscriptions found" while their money sat one tab over.
const BASE_TAB_SEGMENTS = ['all', 'course', 'mobile', 'mock', 'paper'];

const Billing = () => {
  // "refunds" (customer refund requests) only when the backend's
  // REFUND_CUSTOMER_REQUESTS_ENABLED is on; otherwise only admins refund.
  const refundRequestsEnabled = useRefundRequestsEnabled();
  const TAB_SEGMENTS = refundRequestsEnabled ? [...BASE_TAB_SEGMENTS, 'refunds'] : BASE_TAB_SEGMENTS;
  const location = useLocation();
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [parentData, setParentData] = useState(null);
  const [parentStudents, setParentStudents] = useState([]);
  const [assigningPlanKey, setAssigningPlanKey] = useState(null);
  const [payingInstallmentId, setPayingInstallmentId] = useState(null);
  const [fulfillingReturn, setFulfillingReturn] = useState(false);
  const [paymentReturnMessage, setPaymentReturnMessage] = useState(null);
  const [paymentReturnText, setPaymentReturnText] = useState('');
  // Full-page installment payment return: show 'verifying' on first paint when URL has session_id so user always sees the spinner
  const [installmentReturnStatus, setInstallmentReturnStatus] = useState(() => {
    if (typeof window === 'undefined') return null;
    const p = new URLSearchParams(window.location.search);
    if (p.get('payment_canceled') === '1') return null;
    if (p.get('session_id') && p.get('installment_paid') === '1') return 'verifying';
    return null;
  });
  const [installmentReturnError, setInstallmentReturnError] = useState('');
  const fulfilledSessionRef = useRef(null);

  // Get current route segment (course, mock, paper, or mobile)
  const currentRoute = location.pathname.split('/').filter(Boolean).pop() || 'all';
  const isValidTab = TAB_SEGMENTS.includes(currentRoute);
  const activeRoute = isValidTab ? currentRoute : 'all';

  const handleTabChange = (e, newValue) => {
    const segment = TAB_SEGMENTS[newValue] || 'all';
    const base = location.pathname.replace(/\/(all|course|mock|paper|mobile|refunds)$/, '') || `/${process.env.REACT_APP_PARENT_PREFIX || 'parent'}/billing`;
    navigate(`${base}/${segment}`, { replace: true });
  };

  // Debug authentication and get parent data
  useEffect(() => {
    const userData = localStorage.getItem('userData');
    const token = localStorage.getItem('token');
    const adminToken = localStorage.getItem('admin-token');
    const role = localStorage.getItem('role');
    
    let parsedParentData = null;
    if (userData) {
      try {
        parsedParentData = JSON.parse(userData);
        setParentData(parsedParentData);
      } catch (error) {
        console.error('Error parsing userData from localStorage:', error);
      }
    }
    
    console.log('🏠 PARENT PANEL - Authentication Debug:', {
      currentPage: 'Parent Billing Page',
      url: window.location.href,
      userData: parsedParentData,
      parentId: parsedParentData?.id,
      parentName: parsedParentData?.full_name,
      parentEmail: parsedParentData?.email,
      token: token ? 'Present' : 'Missing',
      adminToken: adminToken ? 'Present' : 'Missing',
      role,
      activeToken: adminToken || token || parsedParentData?.access_token
    });
    
    if (!token && !adminToken && !parsedParentData?.access_token) {
      toast.error('No authentication token found. Please log in again.');
    }
  }, []);

  // Redirect to course if on base billing route
  useEffect(() => {
    const path = location.pathname.replace(/\/$/, '');
    const prefix = process.env.REACT_APP_PARENT_PREFIX || 'parent';
    if (path === `/${prefix}/billing` || path.endsWith('/billing')) {
      navigate('all', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Fetch subscriptions when route changes to course
  useEffect(() => {
    if (activeRoute === 'course') {
      fetchSubscriptions();
    }
  }, [activeRoute]);

  // After redirect from Stripe: full-page verifying → fulfill (save to DB) → success or fail screen
  useEffect(() => {
    if (activeRoute !== 'course') return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const installmentPaid = params.get('installment_paid');
    const paymentCanceled = params.get('payment_canceled');

    if (paymentCanceled === '1') {
      setPaymentReturnMessage('info');
      setPaymentReturnText('Payment was canceled.');
      toast.info('Payment was canceled.');
      window.history.replaceState({}, '', location.pathname);
      return;
    }

    if (!sessionId || installmentPaid !== '1') return;
    if (fulfilledSessionRef.current === sessionId) return;
    fulfilledSessionRef.current = sessionId;

    setFulfillingReturn(true);
    setPaymentReturnMessage(null);
    setPaymentReturnText('');
    setInstallmentReturnError('');
    setInstallmentReturnStatus('verifying');

    api
      .post('parent/course/installment/fulfill', { session_id: sessionId })
      .then((res) => {
        setInstallmentReturnStatus('success');
        setPaymentReturnMessage('success');
        setPaymentReturnText(res.data?.message || 'Payment successful. Your installment has been recorded.');
        toast.success(res.data?.message || 'Payment successful.');
        window.history.replaceState({}, '', location.pathname);
        fetchSubscriptions();
      })
      .catch((err) => {
        const msg = err.response?.data?.message || 'Failed to record payment. Please contact support if you were charged.';
        setInstallmentReturnStatus('failed');
        setInstallmentReturnError(msg);
        setPaymentReturnMessage('error');
        setPaymentReturnText(msg);
        toast.error(msg);
        console.error('Installment fulfill failed:', err?.response?.data || err.message);
      })
      .finally(() => setFulfillingReturn(false));
  }, [activeRoute, location.search]);

  // Fetch parent students for course assign dropdown (same as papers)
  useEffect(() => {
    if (activeRoute !== 'course') return;
    const fetchStudents = async () => {
      try {
        const response = await api.get('parent/students/names');
        if (response.data.success && Array.isArray(response.data.data)) {
          setParentStudents(response.data.data);
        }
      } catch (err) {
        console.error('Error fetching parent students for course assign:', err);
      }
    };
    fetchStudents();
  }, [activeRoute]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      // Get parent data from localStorage
      const userData = localStorage.getItem('userData');
      const token = localStorage.getItem('token');
      const role = localStorage.getItem('role');
      
      let parentData = null;
      if (userData) {
        try {
          parentData = JSON.parse(userData);
        } catch (error) {
          console.error('Error parsing userData:', error);
        }
      }
      
      console.log('🔍 DEBUGGING PARENT BILLING:');
      console.log('- Current URL:', window.location.href);
      console.log('- Parent Data:', parentData);
      console.log('- Parent ID:', parentData?.id);
      console.log('- Token:', token ? 'Present' : 'Missing');
      console.log('- Role:', role);
      console.log('- Access Token:', parentData?.access_token ? 'Present' : 'Missing');
      
      if (!parentData?.access_token && !token) {
        toast.error('No authentication token found. Please log in again.');
        return;
      }
      
      console.log('📡 Making API call to: parent/subscriptions');
      
      // Use the parent/subscriptions endpoint as it should automatically use the authenticated parent's ID
      const response = await api.get('parent/subscriptions');
      
      console.log('✅ API Response received:', response.data);
      console.log('- Success:', response.data.success);
      console.log('- Data:', response.data.data);
      console.log('- Message:', response.data.message);
      
      if (response.data.success) {
        const data = response.data.data || [];
        setSubscriptions(Array.isArray(data) ? data : []);
        console.log('📊 Subscriptions set in state:', data);
        
        // Silent loading - no success toast message
        if (!response.data.data || response.data.data.length === 0) {
          console.log('ℹ️ No subscriptions found for this parent');
        }
      } else {
        console.error('❌ API returned success: false', response.data);
        toast.error(response.data.message || 'Failed to load subscriptions');
      }
    } catch (error) {
      console.error('💥 ERROR fetching subscriptions:', error);
      console.error('📋 Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        headers: error.config?.headers
      });
      
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please log in again.');
      } else if (error.response?.status === 404) {
        toast.error('API endpoint not found. Backend may not be running.');
      } else if (error.response?.status === 500) {
        toast.error('Server error. Please check backend logs.');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error('Cannot connect to server. Please check if backend is running.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to load subscription data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePayInstallment = async (parentSubscriptionInstallmentId) => {
    if (!parentSubscriptionInstallmentId) return;
    setPayingInstallmentId(parentSubscriptionInstallmentId);
    try {
      const res = await api.post('parent/course/installment/checkout', {
        parent_subscription_installment_id: parentSubscriptionInstallmentId,
      });
      if (res.data?.success && res.data?.checkout_url) {
        const payUrl = sanitizeStripeCheckoutUrl(res.data.checkout_url);
        if (payUrl) {
          window.location.href = payUrl;
          return;
        }
        toast.error('Invalid payment link. Please try again.');
        return;
      }
      toast.error(res.data?.message || 'Could not start payment.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start payment.');
    } finally {
      setPayingInstallmentId(null);
    }
  };

  const handleAssignCourseToStudent = async (courseId, studentId, coursePriceId) => {
    if (!studentId) return;
    const planKey = coursePriceId != null ? `${courseId}_${coursePriceId}` : `${courseId}_n`;
    setAssigningPlanKey(planKey);
    try {
      const body = {
        course_id: Number(courseId),
        student_ids: [Number(studentId)],
      };
      if (coursePriceId != null) body.course_price_id = Number(coursePriceId);
      const response = await api.post('parent/assign-course-to-student', body);
      if (response.data.success) {
        toast.success(response.data.message || 'Plan assigned to student.');
        await fetchSubscriptions();
      } else {
        toast.error(response.data.message || 'Failed to assign plan.');
      }
    } catch (error) {
      console.error('Error assigning plan to student:', error);
      toast.error(error.response?.data?.message || 'Failed to assign plan.');
    } finally {
      setAssigningPlanKey(null);
    }
  };

  // Full-page: Verifying your payment (after redirect from Stripe – match reference UI)
  if (installmentReturnStatus === 'verifying') {
    return (
      <Container maxWidth="md" sx={{ py: 4, minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 2, color: 'primary.main' }} />
          <Typography variant="body1" sx={{ color: '#374151' }}>
            Verifying your payment...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Full-page: Payment failed
  if (installmentReturnStatus === 'failed') {
    return (
      <Container maxWidth="md" sx={{ py: 4, minHeight: '80vh', display: 'flex', alignItems: 'center' }}>
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} md={10}>
            <Card sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)' }}>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <CancelIcon sx={{ fontSize: 60, color: '#f44336', filter: 'drop-shadow(0 4px 8px rgba(244,67,54,0.3))' }} />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f', mb: 1, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                  Payment Failed
                </Typography>
                <Typography variant="body1" sx={{ color: '#374151', mb: 2, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  {installmentReturnError || 'Your payment could not be processed. Please try again.'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', mt: 3 }}>
                  <Button variant="contained" size="medium" onClick={() => { setInstallmentReturnStatus(null); setInstallmentReturnError(''); fulfilledSessionRef.current = null; window.history.replaceState({}, '', location.pathname); fetchSubscriptions(); }} sx={{ background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 600, borderRadius: '20px', px: 3, py: 1, textTransform: 'none' }}>
                    Try Again
                  </Button>
                  <Button variant="outlined" size="medium" onClick={() => { setInstallmentReturnStatus(null); setInstallmentReturnError(''); fetchSubscriptions(); }} sx={{ borderColor: '#667eea', color: '#667eea', fontWeight: 600, borderRadius: '20px', px: 3, py: 1, textTransform: 'none' }}>
                    Back to Billing
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    );
  }

  // Full-page: Payment successful (matches reference UI: checkmark, success message, next step, buttons)
  if (installmentReturnStatus === 'success') {
    const parentPrefix = process.env.REACT_APP_PARENT_PREFIX || 'parent';
    const parentPortalUrl = typeof window !== 'undefined' ? `${window.location.origin}/${parentPrefix}` : `/${parentPrefix}`;
    return (
      <Container maxWidth="md" sx={{ py: 4, minHeight: '80vh', display: 'flex', alignItems: 'center' }}>
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} md={10}>
            <Card sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50', filter: 'drop-shadow(0 4px 8px rgba(76,175,80,0.3))' }} />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', mb: 1, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                  Payment Successful!
                </Typography>
                <Typography variant="body1" sx={{ color: '#374151', mb: 2, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  🎉 Congratulations! Your item purchase has been completed successfully.
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', mb: 1, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                    Next Step: Enroll Your Student
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#374151', mb: 2, fontSize: { xs: '0.85rem', md: '0.95rem' } }}>
                    Add your student&apos;s details and enroll them to start learning journey.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#374151', mb: 1, fontSize: '0.8rem' }}>
                    If button doesn&apos;t work, copy this URL:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Typography component="span" sx={{ fontSize: '0.75rem', color: '#374151', bgcolor: 'grey.100', px: 1.5, py: 0.75, borderRadius: 1 }}>
                      {parentPortalUrl}
                    </Typography>
                    <Button size="small" variant="outlined" onClick={() => navigator.clipboard.writeText(parentPortalUrl)} sx={{ minWidth: 'auto' }}>
                      Copy
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', mt: 3 }}>
                  <Button variant="contained" size="medium" startIcon={<PersonAddIcon />} onClick={() => { setInstallmentReturnStatus(null); navigate(`/${parentPrefix}/add-student`); }} sx={{ background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 600, borderRadius: '20px', px: 3, py: 1, textTransform: 'none' }}>
                    Add Student Details
                  </Button>
                  <Button variant="outlined" size="medium" onClick={() => { setInstallmentReturnStatus(null); navigate(`/${parentPrefix}`); }} sx={{ borderColor: '#1976d2', color: '#1976d2', fontWeight: 600, borderRadius: '20px', px: 3, py: 1, textTransform: 'none' }}>
                    Back to Home
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      minHeight: 'calc(100vh - 64px)',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      p: { xs: 1.5, sm: 2, md: 3 },
    }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          gap: 2, 
          mb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '25px',
          px: 3,
          py: 1,
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.2)'
        }}>
          <Typography sx={{ fontSize: '20px', color: 'white' }}>💳</Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
            Billing & Subscriptions
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: '#5a6c7d', ml: 1, maxWidth: '100%' }}>
          {activeRoute === 'all' && 'Every payment you have made, most recent first'}
          {activeRoute === 'course' && 'Manage your course subscriptions, view payment history, and download receipts'}
          {activeRoute === 'mock' && 'Manage your mock exam subscriptions and view payment history'}
          {activeRoute === 'paper' && 'View your purchased papers and download PDFs'}
          {activeRoute === 'mobile' && 'View your mobile app subscriptions and payment history'}
          {activeRoute === 'refunds' && 'Ask for a refund on a card payment and follow its progress'}
        </Typography>
        {fulfillingReturn && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Recording your payment… Please wait.
          </Alert>
        )}
        {paymentReturnMessage && paymentReturnText && !fulfillingReturn && (
          <Alert
            severity={paymentReturnMessage === 'error' ? 'error' : paymentReturnMessage === 'success' ? 'success' : 'info'}
            sx={{ mb: 2 }}
            onClose={() => {
              setPaymentReturnMessage(null);
              setPaymentReturnText('');
            }}
          >
            {paymentReturnMessage === 'success' && '✓ '}
            {paymentReturnMessage === 'error' && '✗ '}
            {paymentReturnText}
          </Alert>
        )}
      </Box>

      <Card sx={{ 
        borderRadius: 4,
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(20px)',
        overflow: 'hidden'
      }}>
        <Tabs
          value={Math.max(TAB_SEGMENTS.indexOf(activeRoute), 0)}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 2,
            pt: 1,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.95rem' },
            '& .Mui-selected': { color: '#667eea' },
            '& .MuiTabs-indicator': { bgcolor: '#667eea', height: 3, borderRadius: '3px 3px 0 0' },
          }}
        >
          <Tab label="All" />
          <Tab label="Course" />
          <Tab label="App" />
          <Tab label="Mock Exam" />
          <Tab label="Papers" />
          {refundRequestsEnabled && <Tab label="Refunds" />}
        </Tabs>
        {/* One frame for every tab. Each tab still owns its own table and controls
            (assign-to-student, receipts, request-to-home), but they all sit on the same
            surface with the same padding, so switching tabs no longer changes the layout
            around the content. Padding is tighter on mobile, where px:4 wasted the width. */}
        <Box
          sx={{
            pt: 2,
            px: { xs: 1.5, sm: 2.5, md: 4 },
            pb: 4,
            bgcolor: '#f8fafc',
            minHeight: 400,
            '& > *': { maxWidth: '100%' },
          }}
        >
          {activeRoute === 'all' && (
            <BillingAllPurchases scope="parent" />
          )}
          {activeRoute === 'course' && (
            <>
              <SubscriptionList
                showFilters
                subscriptions={subscriptions} 
                loading={loading}
                parentStudents={parentStudents}
                onAssignCourseToStudent={handleAssignCourseToStudent}
                assigningPlanKey={assigningPlanKey}
                onPayInstallment={handlePayInstallment}
                payingInstallmentId={payingInstallmentId}
              />
              {!loading && subscriptions.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Paying for the mobile app instead? Your app plan and payments are under the{' '}
                  <strong>App Subscription</strong> tab.
                </Alert>
              )}
            </>
          )}
          {activeRoute === 'mock' && (
            <MockExamSubscriptionList />
          )}
          {activeRoute === 'paper' && (
            <PaperPurchases />
          )}
          {activeRoute === 'mobile' && (
            <MobileSubscriptionList />
          )}
          {refundRequestsEnabled && activeRoute === 'refunds' && (
            <ParentRefundRequests />
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default Billing;

